import cloneDeep from 'lodash/cloneDeep';

import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import { fetchEnumsFromSchema } from '@/core/tools/forms/fetch_enums_from_schema';
import { generateFormObject } from '@/core/tools/forms/generate_form_object';
import { prepareFormObjectForSave } from '@/core/tools/forms/prepare_form_object_for_save';
import moment from 'moment';
import {INGREDIENTS_LIST} from "@/modules/ProducedProduct/ProducedProductForm/_store";

interface PrescriptManagerFormState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    document: any;
    form: any;
    ingredients: any[];
    schema: any;
    ingredients_schema: any;

    meta: any;
    targetForDelete: any[];
}

const state: PrescriptManagerFormState = {
    loading: false,
    loaded: false,
    fatalError: false,

    document: null,
    form: null,
    ingredients: [],
    schema: null,
    ingredients_schema: null,

    meta: {},
    targetForDelete: []
};

const initState: PrescriptManagerFormState = cloneDeep(state);

export const PRESCRIPT_MANAGER_DATA = 'GET_PRESCRIPT_MANAGER_DATA';
export const PRESCRIPT_INGREDIENTS_DATA = 'GET_PRESCRIPT_INGREDIENTS_DATA';
export const PRESCRIPT_MANAGER_SCHEMA = 'GET_PRESCRIPT_MANAGER_SCHEMA';
export const PRESCRIPT_INGREDIENTS_SCHEMA = 'PRESCRIPT_INGREDIENTS_SCHEMA';

export const actions: ActionTree<PrescriptManagerFormState, RootState> = {
    reset({ commit }) {
        commit('RESET_STATE')
    },

    init({ commit, dispatch }, params) {
        return dispatch('loadData', params);
    },

    loadData({ commit }, params) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(PRESCRIPT_MANAGER_DATA)
                    .raw('documents.get')
                    .action({
                        id: params.id
                    }),
                vm.$q(PRESCRIPT_INGREDIENTS_DATA)
                    .raw('documents.list')
                    .action({
                        "columns": {
                            "id": "ppi.id",
                            "prescript_id": "ppi.prescript_id",
                            "ingredient_id": "ppi.ingredient_id",
                            "weight": "ppi.weight",
                            "percent": "ppi.percent",
                            "protein": "ppi.protein",
                            "fats": "ppi.fats",
                            "carbohydrate": "ppi.carbohydrate",
                            "protein_percent": "vi.protein",
                            "fats_percent": "vi.fats",
                            "carbohydrate_percent": "vi.carbohydrate",
                        },
                        "from": "s:prescripts_ingredients",
                        "from_as": "ppi",
                        "joins": [
                            {
                                join: 's:voc_ingredients',
                                type: 'left',
                                as: 'vi',
                                on: {
                                    $and: [
                                        {$eq: ['vi.id', 'ppi.ingredient_id']},
                                        {$eq: ['vi.is_vdp', {$: true}]}
                                    ]
                                }
                            }
                        ],
                        "where": {"$eq": ['ppi.prescript_id', {$: params.id}]}
                    }),
                vm.$q(PRESCRIPT_INGREDIENTS_SCHEMA)
                    .raw('schemas.get')
                    .action({
                        id: `s:prescripts_ingredients`
                    }),
            ]).then((result: ServerResult) => {
                commit('SET_FORM_DATA', {
                    document: result.getOperation(PRESCRIPT_MANAGER_DATA).getContent().document,
                    schema: result.getOperation(PRESCRIPT_MANAGER_DATA).getContent().schema,
                    ingredients: result.getOperation(PRESCRIPT_INGREDIENTS_DATA).getContent().documents,
                    ingredients_schema: result.getOperation(PRESCRIPT_INGREDIENTS_SCHEMA).getContent().schema,
                });

                commit('LOADED_SUCCESS');
                resolve(null);
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    prepareNew({ commit, state }, initParams) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(PRESCRIPT_MANAGER_SCHEMA)
                    .raw('schemas.get')
                    .action({
                        id: `s:prescripts`
                    }),
                vm.$q(PRESCRIPT_INGREDIENTS_SCHEMA)
                    .raw('schemas.get')
                    .action({
                        id: `s:prescripts_ingredients`
                    }),
            ]).then((result: ServerResult) => {
                commit('GENERATE_FORM_DATA', {
                    ...result.getOperation(PRESCRIPT_MANAGER_SCHEMA).getContent(),
                    ingredients_schema: result.getOperation(PRESCRIPT_INGREDIENTS_SCHEMA).getContent().schema,
                    initParams
                });

                commit('LOADED_SUCCESS');
                resolve();
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    saveForm({ commit }, documentId) {
        const vm = (this as any)._vm;

        commit('CHANGE_VALUE', {field: 'weight', newValue: state.form.weight});
        const normalizedForm = prepareFormObjectForSave(state.schema, state.form);

        console.log('ingredients_schema', state.ingredients_schema);

        state.ingredients = state.ingredients.map((item: any) => {
            return prepareFormObjectForSave(state.ingredients_schema, item)
        })

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            let saveQuery = null;

            if (documentId) {
                saveQuery = vm.$q('PRESCRIPT_MANAGER_DATA_SAVE')
                    .update(documentId, normalizedForm)
                    .createManyRelation('prescripts_ingredients', 'prescript_id', (createRelated: any) => {
                        return state.ingredients.filter((item: any) => !item.id || (typeof(item.id) !== 'string')).map((item, index) => createRelated(item));
                    })
                    .updateManyRelation('prescripts_ingredients', 'prescript_id', (updateRelated: any) => {
                        return state.ingredients.filter((item: any) => (typeof(item.id) == 'string')).map((item, index) => updateRelated(item))
                    })
                      .deleteManyRelation('prescripts_ingredients', 'prescript_id', (deleteRelated: any) => {
                        return state.targetForDelete.map((item: any, index) => deleteRelated({ id: item }));
                      })

            } else {
                saveQuery = vm.$q('PRESCRIPT_MANAGER_DATA_SAVE')
                    .create('s:prescripts', normalizedForm)
                    .withManyRelation('prescripts_ingredients', 'prescript_id', (createRelated: any) => {
                        return state.ingredients.map((item: any) => createRelated(item));
                    })
            }

            vm.$db([
                saveQuery
            ]).then((result: ServerResult) => {
                const operationResult = result.getOperation('PRESCRIPT_MANAGER_DATA_SAVE');

                if (operationResult.hasError()) {
                    console.log('error', operationResult.getContent());

                    commit('LOADED_SUCCESS');
                    reject();
                } else {
                    commit('ON_FORM_SAVE', operationResult.getContent())

                    commit('LOADED_SUCCESS');
                    resolve(operationResult.getContent());
                }
            })
            .catch((error: any) => {
                console.error('errror', error)
                reject(error);
            });
        })
    },

    changeFormValue({commit, state}, {field, newValue}) {
        commit('CHANGE_VALUE', {field, newValue});
        state.ingredients.map((item: any, index: any) => {
            commit('CHANGE_INGREDIENT_VALUE', {idx: index, field: 'weight', newValue: item.weight});
        })
    },

    changeIngredientValue({commit}, {idx, field, newValue}) {
        commit('CHANGE_INGREDIENT_VALUE', {idx, field, newValue});
    },

    addIngredient({commit}) {
        commit('ADD_INGREDIENT')
    },

    copyIngredient({commit}, {idx}) {
        commit('COPY_INGREDIENT', {idx})
    },


    delIngredient({commit}, {idx}) {
        commit('DEL_INGREDIENT', {idx})
    },

    setArchive({state, rootState, commit}) {
        return new Promise((resolve, reject) => {
            commit('START_LOADING');
            const vm = (this as any)._vm;

            const normalizedForm = prepareFormObjectForSave(state.schema, {
                archived_at: moment().toISOString(),
                archived_user_id: (rootState as any).ubic.actor.document.id
            });
            console.log(normalizedForm)

            const saveQuery = vm.$q('SAVE')
                .update(state.form.id, normalizedForm)
            vm.$db([
                saveQuery
            ]).then(((result: ServerResult) => {
                console.log(result)
                commit('LOADED_SUCCESS');
                resolve();
            }));
        })
    },

    setUnarchive({state, commit}) {
        return new Promise((resolve, reject) => {
            commit('START_LOADING');
            const vm = (this as any)._vm;

            const normalizedForm = prepareFormObjectForSave(state.schema, {
                archived_at: null,
                archived_user_id: null
            });
            normalizedForm['archived_at'] = null;

            const saveQuery = vm.$q('SAVE')
                .update(state.form.id, normalizedForm)
            vm.$db([
                saveQuery
            ]).then(((result: ServerResult) => {
                console.log(result)
                commit('LOADED_SUCCESS');
                resolve();
            }));
        })
    },

};

export const mutations: MutationTree<PrescriptManagerFormState> = {
    RESET_STATE: (state) => {
        for(const key in initState) {
            (state as any)[key] = (initState as any)[key]
        }
    },

    START_LOADING: (state: PrescriptManagerFormState) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },

    LOADED_SUCCESS: (state: PrescriptManagerFormState) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },

    LOADED_ERROR: (state: PrescriptManagerFormState) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_PRESCRIPT_MANAGER_DATA(state: PrescriptManagerFormState, {document}) {
        state.document = document;
    },

    SET_FORM_DATA(state: PrescriptManagerFormState, {document, schema, ingredients, ingredients_schema}) {
        state.document = document;
        state.ingredients = ingredients;
        state.schema = schema;
        state.ingredients_schema = ingredients_schema
        state.meta = fetchEnumsFromSchema(schema);

        state.form = generateFormObject(schema, state.document);
    },

    GENERATE_FORM_DATA(state: PrescriptManagerFormState, {schema, ingredients_schema, initParams}) {
        state.document = {};
        state.schema = schema;
        state.ingredients_schema = ingredients_schema;
        state.meta = fetchEnumsFromSchema(schema);

        state.form = generateFormObject(schema, initParams);
    },

    ON_FORM_SAVE(state: PrescriptManagerFormState, {document, schema}) {
        state.document = document;
        state.schema = schema;

        state.form = generateFormObject(schema, state.document);
    },

    CHANGE_VALUE(state: PrescriptManagerFormState, {field, newValue}) {
        switch (field) {
            default:
                state.form[field] = newValue;
        }
    },

    CHANGE_INGREDIENT_VALUE(state: PrescriptManagerFormState, {idx, field, newValue}) {
        const calc_water: any = () => {
            state.form.water = state.form.total_weight - state.ingredients.reduce((acc: number, item: any) => {
                if (!item.is_composite) {
                  acc += item.weight;
                }
                return acc
            }, 0)
            state.form.water_percent = Math.round(state.form.water / state.form.total_weight * 100 * 10000) / 10000

            state.form.protein = 0
            state.form.fats = 0
            state.form.carbohydrate = 0

            const _data = state.ingredients.reduce((acc: any, item: any) => {
                acc.protein += item.protein
                acc.fats += item.fats
                acc.carbohydrate += item.carbohydrate
                return acc
            }, {protein: 0, fats: 0, carbohydrate: 0})

            state.form.protein = Math.round(_data.protein * 10000) / 10000;
            state.form.fats = Math.round(_data.fats * 10000) / 10000;
            state.form.carbohydrate = Math.round(_data.carbohydrate * 10000) / 10000;
            state.form.calories = Math.round((
                _data.protein / 1 * 4 +
                _data.fats / 1 * 9 +
                _data.carbohydrate / 1 * 4
            )  * 10000) / 10000;
            state.form.energy = Math.round(state.form.calories * 4.19 * 10000) / 10000;
        }
        switch (field) {
            case 'weight':
                state.ingredients[idx].weight = newValue;
                state.ingredients[idx].percent = Math.round(newValue / state.form.total_weight * 100 * 10000) / 10000;
                calc_water()
                break
            case 'percent':
                state.ingredients[idx].percent = newValue;
                state.ingredients[idx].weight = Math.round(state.form.total_weight / 100 * newValue * 10000) / 10000;
                calc_water()
                break
            default:
                state.ingredients[idx][field] = newValue;
        }
        // @ts-ignore
        state.ingredients[idx].protein = state.ingredients[idx].weight * state.ingredients[idx].protein_percent / state.form.total_weight ;
        // @ts-ignore
        state.ingredients[idx].fats = state.ingredients[idx].weight * state.ingredients[idx].fats_percent / state.form.total_weight ;
        // @ts-ignore
        state.ingredients[idx].carbohydrate = state.ingredients[idx].weight * state.ingredients[idx].carbohydrate_percent / state.form.total_weight ;
    },

    ADD_INGREDIENT(state: PrescriptManagerFormState) {
      state.ingredients.push({
          ingredient_id: null,
          weight: 0,
          percent: 0
      })
    },

    COPY_INGREDIENT(state: PrescriptManagerFormState, {idx}) {
        state.ingredients.push({...state.ingredients[idx]})
    },

    DEL_INGREDIENT(state: PrescriptManagerFormState, {idx}) {
        if (state.ingredients[idx].id) {
            state.targetForDelete.push(state.ingredients[idx].id)
        }
        state.ingredients.splice(idx, 1)
    },
};

const getters = {
    fields: (state: PrescriptManagerFormState) => {
        return state.schema.fields.reduce((prev: any, field: any) => {
            prev[field.code] = field;
            return prev;
        }, {});
    },
};

export const STORE_KEY = '$_prescript_manager_form';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
